import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Conversation } from '../../types/messaging';
import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';

interface ConversationItemProps {
    conversation: Conversation;
    onClick?: () => void;
    isActive?: boolean;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
    conversation,
    onClick,
    isActive = false,
}) => {
    const { t } = useTranslation('messaging');
    const navigate = useNavigate();
    const { otherParticipant, lastMessage, lastMessageAt, unreadCount } = conversation;

    const handleAvatarClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent triggering conversation selection
        if (otherParticipant?.id) {
            navigate(`/profile/${otherParticipant.id}`);
        }
    };

    const displayName = otherParticipant?.fullName || otherParticipant?.username || 'Unknown';
    const avatarUrl = otherParticipant?.avatarUrl;
    const initial = displayName.charAt(0).toUpperCase();

    const timeAgo = lastMessageAt
        ? formatDistanceToNow(new Date(lastMessageAt), {
            addSuffix: true,
            locale: getDateFnsLocale(),
        })
        : '';

    // Truncate message preview
    const messagePreview = lastMessage?.content
        ? lastMessage.content.length > 50
            ? lastMessage.content.substring(0, 50) + '...'
            : lastMessage.content
        : t('noMessages', 'No messages yet');

    return (
        <div
            onClick={onClick}
            className={`
                flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors
                ${isActive ? 'bg-primary-50' : 'hover:bg-gray-50'}
                ${(unreadCount ?? 0) > 0 ? 'bg-primary-50/50' : ''}
            `}
        >
            {/* Avatar - clickable to go to profile */}
            <div
                className="relative flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={handleAvatarClick}
                title={t('viewProfile', 'View profile')}
            >
                {avatarUrl ? (
                    <img
                        src={avatarUrl}
                        alt={displayName}
                        className="w-12 h-12 rounded-full object-cover"
                    />
                ) : (
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 font-semibold text-lg">
                        {initial}
                    </div>
                )}

                {/* Online indicator (placeholder) */}
                {/* <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" /> */}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                    <h4 className={`text-sm truncate ${(unreadCount ?? 0) > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                        {displayName}
                        {otherParticipant?.isBot && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-100 text-primary-700 align-middle">
                                {t('aiChatbotLabel', '(AI chatbot)')}
                            </span>
                        )}
                    </h4>
                    <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                        {timeAgo}
                    </span>
                </div>
                <p className={`text-sm truncate mt-0.5 ${(unreadCount ?? 0) > 0 ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                    {messagePreview}
                </p>
            </div>

            {/* Unread Badge */}
            {(unreadCount ?? 0) > 0 && (
                <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary-500 rounded-full">
                        {(unreadCount ?? 0) > 9 ? '9+' : unreadCount}
                    </span>
                </div>
            )}
        </div>
    );
};

export default ConversationItem;
